import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import PopupForm from '../components/PopupForm';
import {
  Shield,
  CheckCircle,
  Award,
  Users,
  TrendingUp,
  FileCheck,
  Search,
  AlertCircle,
  Clock,
  Target,
  Zap,
  Building2,
  ShoppingBag,
  Utensils,
  Laptop,
  Heart,
  GraduationCap,
  Car,
  Home,
  Mail,
  Phone,
  MapPin,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  BookOpen,
  XCircle,
  Layers,
  Star,
  Lightbulb,
  Scale
} from 'lucide-react';

const HomePage = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentPriceIndex, setCurrentPriceIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById('navbar');
      if (window.scrollY > 50) {
        navbar?.classList.add('navbar-scrolled');
      } else {
        navbar?.classList.remove('navbar-scrolled');
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  const pricingPlans = [
    {
      name: 'Essentials',
      price: '$45',
      subtitle: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Basic Search',
        'State E-Filing',
        'Trademark Secured'
      ],
      note: 'Govt. filing fee not included',
      popular: false
    },
    {
      name: 'Enhanced',
      price: '$149',
      subtitle: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter'
      ],
      note: 'Govt. filing fee not included',
      popular: false
    },
    {
      name: 'Deluxe',
      price: '$299',
      subtitle: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager'
      ],
      note: 'Govt. filing fee not included',
      popular: true,
      recommended: true
    },
    {
      name: 'Platinum',
      price: '$349',
      subtitle: '+ Federal Filing Fee',
      features: [
        'Case Review',
        'Case Filing',
        'Direct Hit Search',
        'Trademark Alert',
        'Trademark Secured',
        'High Satisfaction Rate',
        'Refusal Risk Meter',
        'Trademark Monitoring',
        'Complete Documentation',
        'Digital File',
        'Dedicated Case Manager',
        'High Approval Rate',
        'Comprehensive Search Report',
        'High Priority Service',
        'Cease & Desist Letter'
      ],
      note: 'Govt. filing fee not included',
      popular: false
    }
  ];

  const nextPrice = () => {
    setCurrentPriceIndex((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevPrice = () => {
    setCurrentPriceIndex((prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length);
  };

  return (
    <div className="homepage">
      {/* Navigation */}
      <nav id="navbar" className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm transition-all duration-300">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold text-blue-900">
              World Trademark Register
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <button onClick={() => scrollToSection('about')} className="nav-link">About</button>
              <button onClick={() => scrollToSection('services')} className="nav-link">Services</button>
              <button onClick={() => scrollToSection('process')} className="nav-link">Process</button>
              <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="nav-link">Contact</button>
              <Button onClick={() => setIsFormOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
                Get Started
              </Button>
            </div>
            
            {/* Mobile Menu */}
            <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="h-6 w-6 text-blue-900" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[280px] sm:w-[350px]">
                <div className="flex flex-col gap-6 mt-8">
                  <button onClick={() => scrollToSection('about')} className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                    About
                  </button>
                  <button onClick={() => scrollToSection('services')} className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                    Services
                  </button>
                  <button onClick={() => scrollToSection('process')} className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                    Process
                  </button>
                  <button onClick={() => scrollToSection('pricing')} className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                    Pricing
                  </button>
                  <button onClick={() => scrollToSection('faq')} className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                    FAQ
                  </button>
                  <button onClick={() => scrollToSection('contact')} className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600 transition-colors">
                    Contact
                  </button>
                  <Button onClick={() => { setIsFormOpen(true); setIsMobileMenuOpen(false); }} className="bg-amber-600 hover:bg-amber-700 text-white w-full">
                    Get Started
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section pt-32 pb-20 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center">
            <div className="inline-block mb-4 px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-semibold">
              <Shield className="inline w-4 h-4 mr-2" />
              USPTO Filing Experts
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-blue-900 mb-6 leading-tight">
              Protect Your Brand with
              <span className="text-amber-600"> Professional Trademark Registration</span>
            </h1>
            <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
              Secure your intellectual property rights with our expert trademark registration services. Over 1000+ trademarks successfully filed with the USPTO. Fast, reliable, and comprehensive trademark protection for your business.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={() => setIsFormOpen(true)} size="lg" className="bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white px-8 py-6 text-lg">
                Register My Trademark
              </Button>
              <Button onClick={() => scrollToSection('pricing')} size="lg" variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-50 px-8 py-6 text-lg">
                View Pricing
              </Button>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="stat-card">
                <CardContent className="pt-6 text-center">
                  <Award className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">1000+</div>
                  <div className="text-gray-600">Trademarks Filed</div>
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="pt-6 text-center">
                  <Users className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">850+</div>
                  <div className="text-gray-600">Happy Clients</div>
                </CardContent>
              </Card>
              <Card className="stat-card">
                <CardContent className="pt-6 text-center">
                  <TrendingUp className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-blue-900 mb-1">95%</div>
                  <div className="text-gray-600">Success Rate</div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* About Company */}
      <section id="about" className="section-padding bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">About World Trademark Register</h2>
            <div className="title-underline"></div>
          </div>
          <Card className="content-card">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h3 className="text-3xl font-bold text-blue-900 mb-4">Your Trusted Partner in Brand Protection</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    World Trademark Register is a premier legal services provider specializing exclusively in trademark registration and brand protection services across the United States. With years of experience and a deep understanding of USPTO processes, we help businesses of all sizes secure their most valuable asset – their brand identity.
                  </p>
                  <p className="text-gray-700 mb-4 leading-relaxed">
                    Our team of experienced trademark attorneys and specialists has successfully filed over 1,000 trademark applications, maintaining an impressive 95% approval rate. We pride ourselves on delivering personalized service, transparent communication, and comprehensive support throughout the entire trademark registration journey.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    From initial trademark searches to final registration and ongoing monitoring, we provide end-to-end trademark solutions that give you peace of mind and legal protection for your brand. Our mission is to make trademark registration accessible, affordable, and stress-free for entrepreneurs, startups, and established businesses alike.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-6 rounded-lg text-center">
                    <Shield className="w-10 h-10 text-blue-900 mx-auto mb-2" />
                    <div className="font-semibold text-blue-900">Expert Guidance</div>
                  </div>
                  <div className="bg-amber-50 p-6 rounded-lg text-center">
                    <Clock className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                    <div className="font-semibold text-blue-900">Fast Processing</div>
                  </div>
                  <div className="bg-amber-50 p-6 rounded-lg text-center">
                    <Award className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                    <div className="font-semibold text-blue-900">High Success</div>
                  </div>
                  <div className="bg-blue-50 p-6 rounded-lg text-center">
                    <Users className="w-10 h-10 text-blue-900 mx-auto mb-2" />
                    <div className="font-semibold text-blue-900">Dedicated Support</div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trademark Overview */}
      <section className="section-padding">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Understanding Trademark Registration</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4 max-w-3xl mx-auto">
              A trademark is a unique symbol, word, phrase, design, or combination that identifies and distinguishes your products or services from others in the marketplace.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="feature-card">
              <CardHeader>
                <Shield className="w-12 h-12 text-amber-600 mb-4" />
                <CardTitle>What is a Trademark?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  A trademark is your brand's unique identifier – it can be a logo, business name, slogan, or even a specific color scheme. It's the legal foundation that protects your brand identity and prevents others from using confusingly similar marks in your industry.
                </p>
              </CardContent>
            </Card>
            <Card className="feature-card">
              <CardHeader>
                <FileCheck className="w-12 h-12 text-amber-600 mb-4" />
                <CardTitle>Why Register?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Federal trademark registration with the USPTO provides nationwide protection, legal presumption of ownership, the exclusive right to use your mark, and the ability to sue for infringement in federal court. It's essential for building brand value and preventing competitors from copying your identity.
                </p>
              </CardContent>
            </Card>
            <Card className="feature-card">
              <CardHeader>
                <Target className="w-12 h-12 text-amber-600 mb-4" />
                <CardTitle>USPTO Protection</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Registration with the United States Patent and Trademark Office (USPTO) gives you federal-level protection across all 50 states. This means your trademark is protected nationwide, not just in states where you do business, providing the strongest possible legal protection for your brand.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <section id="services" className="section-padding bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Comprehensive Trademark Services</h2>
            <div className="title-underline"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="service-card">
              <CardHeader>
                <Search className="w-10 h-10 text-amber-600 mb-3" />
                <CardTitle>Trademark Search & Clearance</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Before filing, we conduct comprehensive trademark searches to identify potential conflicts with existing registered marks. Our detailed search reports analyze federal, state, and common law trademarks to assess the availability and registrability of your proposed mark.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Direct hit and comprehensive database searches</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Analysis of similar marks and potential conflicts</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Risk assessment and registrability opinions</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardHeader>
                <FileCheck className="w-10 h-10 text-amber-600 mb-3" />
                <CardTitle>Trademark Filing & Registration</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  We handle all aspects of the trademark application process, from preparing and filing your application with the USPTO to responding to office actions and securing final registration. Our experienced team ensures your application is complete, accurate, and optimized for approval.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Expert preparation of trademark applications</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Strategic classification and specimen selection</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Federal and state filing options</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardHeader>
                <AlertCircle className="w-10 h-10 text-amber-600 mb-3" />
                <CardTitle>Office Action Responses</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  If the USPTO issues an office action raising concerns about your application, we provide expert legal responses to overcome refusals and objections. Our team has extensive experience addressing likelihood of confusion refusals, descriptiveness issues, and procedural requirements.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Analysis of office action grounds</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Strategic legal arguments and evidence</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Timely filing within USPTO deadlines</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="service-card">
              <CardHeader>
                <Shield className="w-10 h-10 text-amber-600 mb-3" />
                <CardTitle>Trademark Monitoring & Enforcement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Ongoing trademark monitoring is essential to protect your brand from infringement. We provide continuous watch services to identify potential infringers and take appropriate enforcement action, including cease and desist letters and opposition proceedings when necessary.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Continuous monitoring of new trademark applications</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Cease and desist letter preparation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Enforcement strategy and consultation</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Why Choose World Trademark Register</h2>
            <div className="title-underline"></div>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Award, title: 'Proven Track Record', desc: 'Over 1000+ successful trademark registrations with a 95% approval rate, demonstrating our expertise and attention to detail in every application we file.' },
              { icon: Users, title: 'Expert Legal Team', desc: 'Our team consists of experienced trademark attorneys and specialists who stay current with USPTO rules and regulations to provide you with the best possible service.' },
              { icon: Zap, title: 'Fast Turnaround', desc: 'We prioritize efficiency without sacrificing quality. Most applications are prepared and filed within 24-48 hours of receiving complete information from clients.' },
              { icon: Shield, title: 'Comprehensive Protection', desc: 'From initial search to final registration and ongoing monitoring, we provide complete trademark solutions that protect your brand at every stage.' },
              { icon: Target, title: 'Transparent Pricing', desc: 'No hidden fees or surprises. Our pricing is clear, competitive, and includes all services specified in your chosen package with upfront cost disclosure.' },
              { icon: Clock, title: '24/7 Support', desc: 'Our dedicated case managers are available to answer your questions and provide updates throughout the entire trademark registration process.' }
            ].map((item, idx) => (
              <Card key={idx} className="feature-card text-center">
                <CardContent className="pt-8">
                  <item.icon className="w-14 h-14 text-amber-600 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-blue-900 mb-3">{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="section-padding bg-gradient-to-br from-blue-50 to-amber-50">
        <div className="container mx-auto max-w-5xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Our Simple 3-Step Process</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4">Getting your trademark registered is easier than you think</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '01', icon: Search, title: 'Comprehensive Search', desc: 'We begin with a thorough trademark search to identify any potential conflicts with existing marks. This crucial step helps assess the likelihood of successful registration and identifies any risks before filing. Our search covers federal databases, state registrations, and common law usage to give you a complete picture of your trademark\'s availability.' },
              { step: '02', icon: FileCheck, title: 'Application Filing', desc: 'Once your trademark is cleared, our experts prepare and file a comprehensive application with the USPTO. We carefully select the appropriate trademark classes, prepare accurate descriptions of goods/services, and ensure all specimens and documentation meet USPTO requirements. We handle all the technical details so you don\'t have to worry about compliance issues.' },
              { step: '03', icon: Shield, title: 'Registration & Protection', desc: 'After filing, we monitor your application through the entire USPTO examination process, respond to any office actions, and guide your mark to final registration. Once registered, we provide ongoing monitoring services to protect your trademark from potential infringers and help you maintain your registration with timely renewals and declarations.' }
            ].map((item, idx) => (
              <Card key={idx} className="process-card">
                <CardContent className="p-8">
                  <div className="text-5xl font-bold text-amber-600 mb-4 opacity-20">{item.step}</div>
                  <item.icon className="w-12 h-12 text-blue-900 mb-4" />
                  <h3 className="text-2xl font-bold text-blue-900 mb-4">{item.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{item.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="section-padding">
        <div className="container mx-auto max-w-7xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Transparent Pricing Plans</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4">Choose the perfect package for your trademark needs</p>
          </div>
          
          {/* Mobile Slider */}
          {isMobile ? (
            <div className="relative max-w-sm mx-auto">
              <Card className={`pricing-card ${pricingPlans[currentPriceIndex].recommended ? 'popular-plan' : ''}`}>
                {pricingPlans[currentPriceIndex].recommended && (
                  <div className="recommended-badge">
                    <Award className="w-4 h-4 mr-1" />
                    Recommended
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl font-bold text-blue-900 mb-2">
                    {pricingPlans[currentPriceIndex].name}
                  </CardTitle>
                  <div className="text-4xl font-bold text-amber-600 mb-1">
                    {pricingPlans[currentPriceIndex].price}
                  </div>
                  <CardDescription className="text-sm">
                    {pricingPlans[currentPriceIndex].subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 overflow-y-auto mb-6 pr-2 scrollbar-thin">
                    <ul className="space-y-3">
                      {pricingPlans[currentPriceIndex].features.map((feature, fidx) => (
                        <li key={fidx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mb-4 text-center italic">
                    {pricingPlans[currentPriceIndex].note}
                  </p>
                  <Button onClick={() => setIsFormOpen(true)} className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6">
                    Get Started
                  </Button>
                </CardContent>
              </Card>
              <div className="flex justify-between items-center mt-6">
                <button onClick={prevPrice} className="p-3 rounded-full bg-blue-900 text-white hover:bg-blue-800">
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <div className="flex gap-2">
                  {pricingPlans.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-2 h-2 rounded-full ${idx === currentPriceIndex ? 'bg-blue-900' : 'bg-gray-300'}`}
                    />
                  ))}
                </div>
                <button onClick={nextPrice} className="p-3 rounded-full bg-blue-900 text-white hover:bg-blue-800">
                  <ChevronRight className="w-6 h-6" />
                </button>
              </div>
            </div>
          ) : (
            /* Desktop Grid */
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pricingPlans.map((plan, idx) => (
                <Card key={idx} className={`pricing-card ${plan.recommended ? 'popular-plan' : ''}`}>
                  {plan.recommended && (
                    <div className="recommended-badge">
                      <Award className="w-4 h-4 mr-1" />
                      Recommended
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl font-bold text-blue-900 mb-2">{plan.name}</CardTitle>
                    <div className="text-4xl font-bold text-amber-600 mb-1">{plan.price}</div>
                    <CardDescription className="text-sm">{plan.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80 overflow-y-auto mb-6 pr-2 scrollbar-thin">
                      <ul className="space-y-3">
                        {plan.features.map((feature, fidx) => (
                          <li key={fidx} className="flex items-start">
                            <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className="text-xs text-gray-500 mb-4 text-center italic">{plan.note}</p>
                    <Button onClick={() => setIsFormOpen(true)} className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6">
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Industries Served */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Industries We Serve</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4">Trademark protection for businesses across all sectors</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Laptop, name: 'Technology & Software', desc: 'Protect your tech innovations, software names, and digital brands' },
              { icon: ShoppingBag, name: 'E-commerce & Retail', desc: 'Secure your online store names, product lines, and retail brands' },
              { icon: Utensils, name: 'Food & Beverage', desc: 'Register restaurant names, food products, and beverage brands' },
              { icon: Heart, name: 'Health & Wellness', desc: 'Trademark medical devices, supplements, and wellness services' },
              { icon: Building2, name: 'Professional Services', desc: 'Protect consulting firms, agencies, and service provider brands' },
              { icon: GraduationCap, name: 'Education & Training', desc: 'Register educational programs, courses, and training brands' },
              { icon: Car, name: 'Automotive', desc: 'Secure auto parts, accessories, and automotive service brands' },
              { icon: Home, name: 'Real Estate', desc: 'Protect property development names and real estate service brands' }
            ].map((industry, idx) => (
              <Card key={idx} className="industry-card text-center hover:shadow-lg transition-all duration-300">
                <CardContent className="pt-6">
                  <industry.icon className="w-12 h-12 text-amber-600 mx-auto mb-3" />
                  <h3 className="font-bold text-blue-900 mb-2">{industry.name}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{industry.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Benefits of Trademark Registration</h2>
            <div className="title-underline"></div>
          </div>
          <Card className="content-card">
            <CardContent className="p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: Shield, title: 'Legal Protection', desc: 'Federal trademark registration provides legal presumption of ownership and exclusive rights to use your mark nationwide in connection with your goods or services. This makes it much easier to enforce your rights against infringers.' },
                  { icon: Award, title: 'Brand Value', desc: 'A registered trademark is a valuable business asset that increases in value over time. It can be licensed, franchised, or sold, providing additional revenue streams and enhancing your company\'s overall valuation.' },
                  { icon: Target, title: 'Market Exclusivity', desc: 'Registration prevents competitors from using confusingly similar marks, giving you exclusive rights in your market segment. This helps establish and maintain your unique position in the marketplace.' },
                  { icon: TrendingUp, title: 'Business Growth', desc: 'A strong trademark makes it easier to expand into new markets, attract investors, and build customer loyalty. It serves as a foundation for all your marketing and branding efforts.' },
                  { icon: FileCheck, title: 'Legal Deterrent', desc: 'The ® symbol sends a clear message to potential infringers that your mark is protected. Registration also enables you to seek statutory damages and attorney\'s fees in infringement lawsuits.' },
                  { icon: Zap, title: 'Online Protection', desc: 'Registered trademarks help you combat cybersquatting, protect your domain names, and enforce your rights on social media platforms and e-commerce marketplaces like Amazon.' }
                ].map((benefit, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <benefit.icon className="w-10 h-10 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">{benefit.title}</h3>
                      <p className="text-gray-700 leading-relaxed">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="section-padding bg-gradient-to-br from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Trusted by Businesses Nationwide</h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
          </div>
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="achievement-item">
              <div className="text-5xl font-bold text-amber-400 mb-2">1000+</div>
              <div className="text-xl">Trademarks Successfully Filed</div>
            </div>
            <div className="achievement-item">
              <div className="text-5xl font-bold text-amber-400 mb-2">95%</div>
              <div className="text-xl">USPTO Approval Rate</div>
            </div>
            <div className="achievement-item">
              <div className="text-5xl font-bold text-amber-400 mb-2">850+</div>
              <div className="text-xl">Satisfied Clients</div>
            </div>
            <div className="achievement-item">
              <div className="text-5xl font-bold text-amber-400 mb-2">24/7</div>
              <div className="text-xl">Expert Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="section-padding">
        <div className="container mx-auto max-w-4xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <div className="title-underline"></div>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {[
              { q: 'What is the difference between a trademark and a copyright?', a: 'A trademark protects brand names, logos, and slogans used to identify goods or services. Copyright protects original works of authorship like books, music, and art. Trademarks protect your brand identity, while copyrights protect creative content. Both serve different purposes in intellectual property protection.' },
              { q: 'How long does the trademark registration process take?', a: 'The USPTO trademark registration process typically takes 8-12 months from filing to final registration, assuming no objections or office actions. However, your mark receives a filing date immediately upon submission, and you can use "TM" while pending. We monitor your application throughout and handle any issues that arise to expedite the process.' },
              { q: 'How much does trademark registration cost?', a: 'Our service fees range from $45 to $349 depending on the package you choose. Additionally, USPTO filing fees ($250-$350 per class) apply. We provide transparent pricing with no hidden fees. The total cost depends on the number of trademark classes your goods/services fall under and the level of service you select.' },
              { q: 'Can I trademark a business name?', a: 'Yes, you can trademark a business name if you use it to identify your goods or services. However, the name must be distinctive and not merely descriptive of what you sell. Generic terms cannot be trademarked. We conduct a comprehensive search to determine if your business name is eligible for trademark protection.' },
              { q: 'Do I need a trademark attorney to register my trademark?', a: 'While not legally required, working with trademark professionals significantly increases your chances of successful registration. The USPTO reports that applications filed by attorneys have higher approval rates. Our experienced team knows how to navigate complex USPTO requirements, respond to office actions, and avoid common mistakes that lead to rejection.' },
              { q: 'What is the difference between ™ and ®?', a: 'The ™ symbol indicates you are claiming trademark rights but the mark is not yet registered with the USPTO. You can use ™ immediately, even while your application is pending. The ® symbol can only be used after your trademark is officially registered with the USPTO. Improper use of ® before registration can result in denial of your application.' },
              { q: 'How long does trademark protection last?', a: 'Federal trademark registration lasts for 10 years and can be renewed indefinitely for successive 10-year periods, as long as you continue using the mark in commerce. You must file maintenance documents between the 5th and 6th year after registration, and then renew every 10 years. We provide renewal reminder services to help you maintain your registration.' },
              { q: 'What happens if someone is already using a similar trademark?', a: 'This is why our comprehensive trademark search is crucial before filing. If we find conflicting marks, we assess the likelihood of confusion based on similarity of marks, related goods/services, and channels of trade. We may recommend modifications to your mark, pursuing a narrower scope of protection, or selecting an alternative mark. Our goal is to find the best path forward for your brand protection needs.' }
            ].map((faq, idx) => (
              <AccordionItem key={idx} value={`item-${idx}`} className="faq-item">
                <AccordionTrigger className="text-left text-lg font-semibold text-blue-900 hover:text-amber-600">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-gray-700 leading-relaxed pt-2">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* NEW SECTION 1: How Trademarks Work */}
      <section className="section-padding bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">How Trademarks Work: A Complete Guide</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4 max-w-3xl mx-auto">
              Understanding the trademark system is essential for protecting your brand. Here's everything you need to know about how trademarks function in the United States.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <Card className="content-card">
              <CardHeader>
                <BookOpen className="w-12 h-12 text-amber-600 mb-4" />
                <CardTitle>What Makes a Trademark Protectable?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Not all marks can be registered as trademarks. The USPTO evaluates trademarks on a spectrum of distinctiveness, ranging from generic (unprotectable) to fanciful (strongest protection). Understanding where your mark falls on this spectrum is crucial for successful registration.
                </p>
                <div className="space-y-3">
                  <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                    <div className="font-semibold text-red-900 mb-1">Generic (Not Protectable)</div>
                    <p className="text-sm text-gray-700">Common names for products/services. Example: "Computer" for computers, "Coffee Shop" for cafes.</p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-500">
                    <div className="font-semibold text-orange-900 mb-1">Descriptive (Weak Protection)</div>
                    <p className="text-sm text-gray-700">Describes product features. Example: "Quick-Dry" for towels. Requires proof of acquired distinctiveness.</p>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                    <div className="font-semibold text-yellow-900 mb-1">Suggestive (Moderate Protection)</div>
                    <p className="text-sm text-gray-700">Hints at qualities without describing. Example: "Coppertone" for suntan lotion, "Netflix" for streaming.</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                    <div className="font-semibold text-green-900 mb-1">Arbitrary (Strong Protection)</div>
                    <p className="text-sm text-gray-700">Common words in unrelated context. Example: "Apple" for computers, "Amazon" for e-commerce.</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                    <div className="font-semibold text-blue-900 mb-1">Fanciful (Strongest Protection)</div>
                    <p className="text-sm text-gray-700">Invented words with no other meaning. Example: "Xerox," "Kodak," "Google."</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="content-card">
              <CardHeader>
                <Layers className="w-12 h-12 text-amber-600 mb-4" />
                <CardTitle>Understanding Trademark Classes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-4">
                  The USPTO organizes goods and services into 45 international classes (34 for goods, 11 for services). Selecting the correct classes is critical for proper trademark protection and affects both filing fees and the scope of your rights.
                </p>
                <div className="space-y-3">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="font-semibold text-blue-900 mb-2 flex items-center">
                      <Target className="w-5 h-5 mr-2" />
                      Popular Goods Classes
                    </div>
                    <ul className="text-sm text-gray-700 space-y-1 ml-7">
                      <li><strong>Class 9:</strong> Computer software, mobile apps, electronics</li>
                      <li><strong>Class 25:</strong> Clothing, footwear, headwear</li>
                      <li><strong>Class 30:</strong> Coffee, tea, baked goods, confectionery</li>
                      <li><strong>Class 35:</strong> Retail and wholesale services</li>
                    </ul>
                  </div>
                  <div className="bg-amber-50 p-4 rounded-lg">
                    <div className="font-semibold text-amber-900 mb-2 flex items-center">
                      <Target className="w-5 h-5 mr-2" />
                      Popular Service Classes
                    </div>
                    <ul className="text-sm text-gray-700 space-y-1 ml-7">
                      <li><strong>Class 41:</strong> Education, entertainment, sporting events</li>
                      <li><strong>Class 42:</strong> Technology services, software development</li>
                      <li><strong>Class 43:</strong> Restaurant and hotel services</li>
                      <li><strong>Class 44:</strong> Medical, beauty, and wellness services</li>
                    </ul>
                  </div>
                  <p className="text-sm text-gray-600 italic mt-4">
                    <Lightbulb className="inline w-4 h-4 mr-1" />
                    Pro Tip: Each additional class increases USPTO filing fees by $250-$350. Choose carefully to balance protection and cost.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="content-card">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <FileCheck className="w-5 h-5 text-blue-900" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-900">Use in Commerce</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    To maintain your trademark, you must use it in interstate commerce. This means selling goods or providing services across state lines. Simple registration isn't enough—actual commercial use is required to keep your trademark valid and enforceable.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-900">Maintenance Requirements</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Trademarks require ongoing maintenance. File a Declaration of Use between years 5-6 after registration, then renew every 10 years. Missing these deadlines can result in cancellation of your trademark, losing all protection you've built.
                  </p>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-green-600" />
                    </div>
                    <h3 className="text-xl font-bold text-blue-900">Enforcement Rights</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    Registration gives you the right to sue infringers in federal court, claim statutory damages, and use the ® symbol. You can also record your trademark with U.S. Customs to prevent counterfeit imports and enforce your rights on online marketplaces.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* NEW SECTION 2: Common Trademark Mistakes */}
      <section className="section-padding bg-white">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Common Trademark Mistakes to Avoid</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4 max-w-3xl mx-auto">
              Learn from others' mistakes. These common errors cost businesses thousands in legal fees and lost trademark rights every year.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Card className="service-card border-l-4 border-red-500">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-red-900">Filing Too Late</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Many businesses wait until after launching to file for trademark protection, only to discover someone else has already registered a similar mark. This can force costly rebranding or legal disputes.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="font-semibold text-green-900 mb-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    The Right Way
                  </div>
                  <p className="text-sm text-gray-700">
                    File your trademark application as soon as you've selected your brand name, even before launch. You can file an "intent to use" application to secure your filing date while you prepare for business launch.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="service-card border-l-4 border-red-500">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-red-900">Choosing Generic Names</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Selecting a descriptive or generic name like "Best Coffee" or "Quick Cleaners" makes trademark protection nearly impossible. These names are either unregistrable or very weak, offering little legal protection.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="font-semibold text-green-900 mb-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    The Right Way
                  </div>
                  <p className="text-sm text-gray-700">
                    Choose distinctive, creative names that don't directly describe your product or service. Think "Starbucks" instead of "Coffee House" or "Amazon" instead of "Online Bookstore."
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="service-card border-l-4 border-red-500">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-red-900">Skipping the Trademark Search</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Filing without a comprehensive search is gambling with your business future. The USPTO will reject your application if conflicting marks exist, wasting time and money while leaving you unprotected.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="font-semibold text-green-900 mb-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    The Right Way
                  </div>
                  <p className="text-sm text-gray-700">
                    Always conduct a comprehensive trademark search before filing. This includes federal, state, and common law searches to identify potential conflicts. Our professional search reports help you make informed decisions.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="service-card border-l-4 border-red-500">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-red-900">Wrong Trademark Class Selection</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Selecting incorrect or insufficient trademark classes leaves gaps in your protection. Competitors can register similar marks in classes you overlooked, limiting your ability to expand your business.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="font-semibold text-green-900 mb-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    The Right Way
                  </div>
                  <p className="text-sm text-gray-700">
                    Work with trademark professionals to identify all relevant classes for your current business and planned expansion. Consider both goods and services classes to ensure comprehensive protection.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="service-card border-l-4 border-red-500">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-red-900">Ignoring Office Actions</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  USPTO office actions require timely, professional responses. Missing deadlines or submitting inadequate responses leads to application abandonment, forfeiting your filing date and leaving your brand unprotected.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="font-semibold text-green-900 mb-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    The Right Way
                  </div>
                  <p className="text-sm text-gray-700">
                    Respond to all USPTO office actions within the deadline (typically 6 months) with well-crafted legal arguments. Our team handles office action responses to maximize your approval chances.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="service-card border-l-4 border-red-500">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <XCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <CardTitle className="text-red-900">Neglecting Trademark Maintenance</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Trademarks require ongoing maintenance filings at specific intervals. Missing the 5-6 year Declaration of Use or 10-year renewal results in automatic cancellation, losing all trademark rights you've built.
                </p>
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                  <div className="font-semibold text-green-900 mb-1 flex items-center">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    The Right Way
                  </div>
                  <p className="text-sm text-gray-700">
                    Set up trademark monitoring and renewal services to track maintenance deadlines. Our monitoring service sends timely reminders and handles all required filings to keep your trademark active.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* NEW SECTION 3: Trademark vs Other IP Protection */}
      <section className="section-padding bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Trademark vs. Other Intellectual Property Protection</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4 max-w-3xl mx-auto">
              Understanding the differences between trademarks, copyrights, and patents helps you choose the right protection for your business assets.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card className="content-card bg-blue-50 border-2 border-blue-200">
              <CardHeader>
                <Shield className="w-12 h-12 text-blue-900 mb-4" />
                <CardTitle className="text-blue-900">Trademarks</CardTitle>
                <CardDescription className="text-blue-700">Protect Brand Identity</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="font-semibold text-blue-900 mb-1">What They Protect:</div>
                    <p className="text-sm text-gray-700">Brand names, logos, slogans, product packaging, and any distinctive identifier of goods/services.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-900 mb-1">Duration:</div>
                    <p className="text-sm text-gray-700">Potentially indefinite, as long as you continue using the mark in commerce and file required maintenance documents.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-900 mb-1">Best For:</div>
                    <p className="text-sm text-gray-700">Businesses wanting to protect their brand reputation, prevent customer confusion, and build brand equity.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-blue-900 mb-1">Example:</div>
                    <p className="text-sm text-gray-700 italic">"Nike" name and swoosh logo, "Just Do It" slogan, Coca-Cola bottle shape.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="content-card bg-purple-50 border-2 border-purple-200">
              <CardHeader>
                <BookOpen className="w-12 h-12 text-purple-900 mb-4" />
                <CardTitle className="text-purple-900">Copyrights</CardTitle>
                <CardDescription className="text-purple-700">Protect Creative Works</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="font-semibold text-purple-900 mb-1">What They Protect:</div>
                    <p className="text-sm text-gray-700">Original works of authorship including books, music, art, photography, software code, and architectural works.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-purple-900 mb-1">Duration:</div>
                    <p className="text-sm text-gray-700">Life of author plus 70 years (for individuals) or 95 years from publication (for corporations).</p>
                  </div>
                  <div>
                    <div className="font-semibold text-purple-900 mb-1">Best For:</div>
                    <p className="text-sm text-gray-700">Artists, writers, musicians, software developers, and creators of original expressive content.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-purple-900 mb-1">Example:</div>
                    <p className="text-sm text-gray-700 italic">Books, songs, movies, website content, software applications, graphic designs.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="content-card bg-green-50 border-2 border-green-200">
              <CardHeader>
                <Lightbulb className="w-12 h-12 text-green-900 mb-4" />
                <CardTitle className="text-green-900">Patents</CardTitle>
                <CardDescription className="text-green-700">Protect Inventions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <div className="font-semibold text-green-900 mb-1">What They Protect:</div>
                    <p className="text-sm text-gray-700">New, useful, and non-obvious inventions, processes, machines, or improvements to existing technology.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-green-900 mb-1">Duration:</div>
                    <p className="text-sm text-gray-700">20 years from filing date for utility patents, 15 years for design patents. No renewal possible.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-green-900 mb-1">Best For:</div>
                    <p className="text-sm text-gray-700">Inventors, manufacturers, and technology companies with novel innovations or improvements.</p>
                  </div>
                  <div>
                    <div className="font-semibold text-green-900 mb-1">Example:</div>
                    <p className="text-sm text-gray-700 italic">New drug formulas, mechanical devices, software algorithms, manufacturing processes.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="content-card">
            <CardHeader>
              <CardTitle className="text-2xl">Can You Have Multiple Types of Protection?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed mb-4">
                Absolutely! Many successful businesses use multiple forms of intellectual property protection simultaneously. In fact, layered protection often provides the most comprehensive defense for your business assets.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="font-semibold text-blue-900 mb-3 flex items-center">
                    <Scale className="w-5 h-5 mr-2" />
                    Real-World Example: Apple Inc.
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Trademarks:</strong> "Apple," "iPhone," "iOS" names and logos</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Patents:</strong> Touch screen technology, Face ID, device designs</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span><strong>Copyrights:</strong> iOS software code, user interface designs, marketing materials</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-amber-50 p-6 rounded-lg">
                  <div className="font-semibold text-amber-900 mb-3 flex items-center">
                    <Lightbulb className="w-5 h-5 mr-2" />
                    Recommended Strategy for Startups
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <span className="font-semibold text-amber-900 mr-2">1.</span>
                      <span>Start with trademark protection for your brand name and logo (most affordable and immediately useful)</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-amber-900 mr-2">2.</span>
                      <span>Register copyrights for your website content, marketing materials, and software</span>
                    </li>
                    <li className="flex items-start">
                      <span className="font-semibold text-amber-900 mr-2">3.</span>
                      <span>Pursue patents if you have truly novel inventions or technology (most expensive but valuable for tech companies)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* NEW SECTION 4: Success Stories */}
      <section className="section-padding bg-gradient-to-br from-blue-900 to-blue-700 text-white">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Client Success Stories</h2>
            <div className="w-24 h-1 bg-amber-600 mx-auto mb-6"></div>
            <p className="text-blue-100 text-lg max-w-3xl mx-auto">
              Real businesses, real results. See how trademark protection helped our clients secure their brands and grow their businesses with confidence.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-xl">TechFlow Solutions</div>
                    <div className="text-sm text-blue-200">Software Startup</div>
                  </div>
                </div>
                <p className="text-blue-50 leading-relaxed mb-4">
                  "We were about to launch when we discovered another company had a similar name. World Trademark Register conducted a comprehensive search, helped us rebrand strategically, and filed our trademark before launch. Three years later, we've expanded into 10 states with complete confidence in our brand protection."
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">$250K</div>
                    <div className="text-xs text-blue-200">Saved in Rebranding</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">10</div>
                    <div className="text-xs text-blue-200">States Protected</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">2</div>
                    <div className="text-xs text-blue-200">Infringement Cases Won</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-xl">Bella's Boutique</div>
                    <div className="text-sm text-blue-200">Fashion Retail</div>
                  </div>
                </div>
                <p className="text-blue-50 leading-relaxed mb-4">
                  "I thought trademark registration was only for big corporations. World Trademark Register made it affordable and simple for my small boutique. When a competitor opened using a similar name, my trademark gave me the legal standing to protect my brand. Now I'm franchising with confidence!"
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">5</div>
                    <div className="text-xs text-blue-200">Franchise Locations</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">3</div>
                    <div className="text-xs text-blue-200">Trademarks Registered</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">100%</div>
                    <div className="text-xs text-blue-200">Brand Control</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/15 transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-xl">HealthPro Wellness</div>
                    <div className="text-sm text-blue-200">Healthcare Services</div>
                  </div>
                </div>
                <p className="text-blue-50 leading-relaxed mb-4">
                  "Our rapid growth meant expanding to multiple states quickly. World Trademark Register handled our complete trademark portfolio—business name, three service lines, and logo. Their monitoring service alerted us to a potential infringer, and we resolved it before it became costly. Invaluable peace of mind."
                </p>
                <div className="flex items-center gap-4 pt-4 border-t border-white/20">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">15</div>
                    <div className="text-xs text-blue-200">States Operating</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">4</div>
                    <div className="text-xs text-blue-200">Trademarks Filed</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-400">24/7</div>
                    <div className="text-xs text-blue-200">Monitoring Active</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <Button onClick={() => setIsFormOpen(true)} size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-10 py-7 text-lg shadow-xl">
              Start Your Success Story Today
            </Button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-amber-600 to-amber-500">
        <div className="container mx-auto max-w-4xl px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Protect Your Brand Today?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Don't wait until it's too late. Secure your trademark now and protect your brand's future. Our experts are ready to guide you through every step of the registration process.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button onClick={() => setIsFormOpen(true)} size="lg" className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-7 text-lg shadow-xl">
              Register My Trademark Now
            </Button>
            <Button onClick={() => scrollToSection('contact')} size="lg" variant="outline" className="bg-white text-amber-600 hover:bg-gray-50 border-2 border-white px-10 py-7 text-lg shadow-xl">
              Contact Us
            </Button>
          </div>
          <p className="mt-8 text-white/80 text-sm">
            <Clock className="inline w-4 h-4 mr-1" />
            Limited Time: Get 20% off Deluxe & Platinum packages
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="section-padding bg-gray-50">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="section-title">Get in Touch</h2>
            <div className="title-underline"></div>
            <p className="text-gray-600 mt-4">Have questions? We're here to help</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="contact-card">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-blue-900 mb-1">Our Office</div>
                      <p className="text-gray-700">40 Wall St 33rd Floor, Suite 3302<br />New York, NY 10005</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-blue-900 mb-1">Phone</div>
                      <p className="text-gray-700">+1 (123) 123-4567</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold text-blue-900 mb-1">Email</div>
                      <p className="text-gray-700">info@worldtrademarkregister.com</p>
                    </div>
                  </div>
                </div>
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <h4 className="font-semibold text-blue-900 mb-3">Business Hours</h4>
                  <p className="text-gray-700">Monday - Friday: 9:00 AM - 6:00 PM EST</p>
                  <p className="text-gray-700">Saturday: 10:00 AM - 4:00 PM EST</p>
                  <p className="text-gray-700">Sunday: Closed</p>
                </div>
              </CardContent>
            </Card>
            <Card className="contact-card">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-blue-900 mb-6">Send Us a Message</h3>
                <p className="text-gray-600 mb-6">Click the button below to open our contact form and one of our trademark experts will respond within 24 hours.</p>
                <Button onClick={() => setIsFormOpen(true)} size="lg" className="w-full bg-gradient-to-r from-blue-900 to-blue-700 hover:from-blue-800 hover:to-blue-600 text-white py-6 text-lg mb-6">
                  Open Contact Form
                </Button>
                <div className="bg-blue-50 rounded-lg p-6">
                  <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-amber-600" />
                    Why Contact Us?
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Free trademark consultation</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Expert guidance on your specific needs</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Fast response within 24 hours</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>No obligation to proceed</span>
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-900 text-white py-12">
        <div className="container mx-auto max-w-6xl px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">World Trademark Register</h3>
              <p className="text-blue-200 leading-relaxed">
                Your trusted partner in trademark registration and brand protection services.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-blue-200">
                <li><button onClick={() => scrollToSection('about')} className="hover:text-white transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-white transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-white transition-colors">FAQ</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-blue-200">
                <li><a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms-conditions" className="hover:text-white transition-colors">Terms & Conditions</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-blue-200">
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>40 Wall St 33rd Floor, Suite 3302, New York, NY 10005</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  <span>+1 (123) 123-4567</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  <span>info@worldtrademarkregister.com</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-blue-800 pt-8 text-center text-blue-200">
            <p>© 2026 World Trademark Register. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <PopupForm isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
};

export default HomePage;
